import { z } from 'zod';
const LINE = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_.-]*)\s*=\s*(.*)$/;
export function parseDotenv(text) {
    const byKey = new Map();
    const problems = [];
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    for (let index = 0; index < lines.length; index += 1) {
        const raw = lines[index] ?? '';
        const lineNumber = index + 1;
        const trimmed = raw.trim();
        if (trimmed === '' || trimmed.startsWith('#'))
            continue;
        const match = LINE.exec(raw);
        if (!match) {
            problems.push({ line: lineNumber, reason: 'Not a KEY=value line' });
            continue;
        }
        const key = match[1];
        const rest = match[2] ?? '';
        const value = readValue(rest);
        if (value === undefined) {
            problems.push({ line: lineNumber, reason: `The quote around ${key} is never closed` });
            continue;
        }
        if (byKey.has(key)) {
            problems.push({
                line: lineNumber,
                reason: `${key} appears more than once; the later line is the one that counts`,
            });
        }
        byKey.set(key, { key, value, line: lineNumber });
    }
    return { entries: [...byKey.values()], problems };
}
function readValue(rest) {
    const value = rest.trim();
    if (value.startsWith('"')) {
        let out = '';
        for (let position = 1; position < value.length; position += 1) {
            const character = value[position];
            if (character === '\\' && position + 1 < value.length) {
                const next = value[position + 1];
                out += next === 'n' ? '\n' : next === 't' ? '\t' : next === 'r' ? '\r' : next;
                position += 1;
                continue;
            }
            if (character === '"')
                return out;
            out += character;
        }
        return undefined;
    }
    if (value.startsWith("'")) {
        const end = value.indexOf("'", 1);
        return end === -1 ? undefined : value.slice(1, end);
    }
    // Unquoted: a comment starts only at whitespace followed by `#`, so a URL
    // fragment or a colour code survives.
    const comment = value.search(/\s#/);
    return (comment === -1 ? value : value.slice(0, comment)).trim();
}
// ---------------------------------------------------------------------------
// The import request and its answer
// ---------------------------------------------------------------------------
/** Enough for any real `.env`; a ceiling so a paste cannot be a denial of service. */
export const MAX_DOTENV_BYTES = 64 * 1024;
export const importEnvironmentRequestSchema = z.object({
    text: z.string().min(1, 'Paste the contents of a .env file').max(MAX_DOTENV_BYTES),
    /**
     * Where the values go.
     *
     * A real `.env` usually contains credentials, and importing those as plain
     * variables would put them where they are returned by an endpoint and shown on
     * a page. The choice is explicit so nobody does that by accident.
     */
    as: z.enum(['variables', 'secrets']),
});
export const importEnvironmentResponseSchema = z.object({
    /** The names that were set. */
    applied: z.array(z.string()),
    /** The lines that were not, and why, in words a person can act on. */
    refused: z.array(z.object({ key: z.string().nullable(), line: z.number(), reason: z.string() })),
});
//# sourceMappingURL=dotenv.js.map